export type ResourceStatus = 'loading' | 'ready' | 'missing' | 'error';
export interface UserProfile {
    id: string;
    /** Legacy callers may provide this; sign-in email is always read from Auth. */
    email?: string;
    full_name?: string | null;
    phone?: string | null;
    avatar_url?: string | null;
    avatar_path?: string | null;
    company?: string | null;
    /** Descriptive job title, never an authorization role. */
    role?: string | null;
    bio?: string | null;
    revision?: number;
}

export interface ProfileDraft { fullName: string; phone: string; jobTitle: string; company: string; bio: string }
export type ProfileErrors = Partial<Record<keyof ProfileDraft, string>>;
export type ProfilePatch = Partial<Pick<UserProfile, 'full_name' | 'phone' | 'role' | 'company' | 'bio'>>;
export const toProfileDraft = (profile: UserProfile): ProfileDraft => ({
    fullName: profile.full_name || '', phone: profile.phone || '', jobTitle: profile.role || '',
    company: profile.company || '', bio: profile.bio || '',
});
export const sameDraft = (a: ProfileDraft, b: ProfileDraft) => (Object.keys(a) as (keyof ProfileDraft)[]).every(key => a[key] === b[key]);

export class AccountError extends Error {
    constructor(public code: 'conflict' | 'unavailable' | 'invalid' | 'missing' | 'auth', message: string) {
        super(message); this.name = 'AccountError';
    }
}
export const accountError = (error: { code?: string } | null | undefined): AccountError => {
    if (error?.code === '40001') return new AccountError('conflict', 'This account changed in another tab. Review the latest details before saving again.');
    if (error?.code === '42501') return new AccountError('auth', 'Your session could not be verified. Sign in again and retry.');
    if (error?.code === '22023' || error?.code === '23514') return new AccountError('invalid', 'Some details could not be accepted. Check your changes and try again.');
    return new AccountError('unavailable', 'Account settings could not be updated. Your changes are still here; try again.');
};

export function readProfile(value: unknown, userId?: string): UserProfile {
    if (!value || typeof value !== 'object' || typeof (value as UserProfile).id !== 'string' ||
        (userId && (value as UserProfile).id !== userId)) throw new AccountError('unavailable', 'Profile details could not be loaded.');
    return value as UserProfile;
}
